// PipelinesScreen — director-only funnel/stage configuration screen.
// Mirrors the structure & styling of AiSettingsScreen (same design tokens,
// same card/list patterns, same mutation → replace-state-from-response flow).
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button, Icon, SectionHeader, ScreenSkeleton, useToast, t } from '../../design'
import { CardSectionsEditor } from '../../components/director/CardSectionsEditor'
import {
  type Pipeline,
  type PipelineStage,
  fetchPipelines,
  importPipelines,
  createPipeline,
  patchPipeline,
  createStage,
  reorderStages,
  patchStage,
  deleteStage,
} from '../../api/pipelines'

// ─────────────────────────────────────────────────────────────────────────────
// Shared style constants (mirrors AiSettingsScreen)
// ─────────────────────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: 10,
  font: '400 14px/1.4 var(--font-sans)',
  border: '1px solid var(--border)', background: 'var(--surface-1)', color: 'var(--text-1)',
  boxSizing: 'border-box',
}

const iconWrap: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 9,
  background: 'var(--brand-100)', color: 'var(--brand-900)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function Phase2Badge() {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: '#b45309',
      background: '#fef9c3', borderRadius: 6, padding: '2px 6px', marginLeft: 6,
    }}>
      {t('pip.phase2')}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage editor
// ─────────────────────────────────────────────────────────────────────────────
function StageEditor({
  pipeline,
  stage,
  allStages,
  onClose,
  onUpdate,
}: {
  pipeline: Pipeline
  stage: PipelineStage
  allStages: PipelineStage[]
  onClose: () => void
  onUpdate: (funnels: Pipeline[]) => void
}) {
  const toast = useToast()
  const [displayName, setDisplayName] = useState(stage.display_name)
  const [color, setColor] = useState(stage.color ?? '')
  const [semantics, setSemantics] = useState(stage.semantics ?? '')

  const patchMut = useMutation({
    mutationFn: (patch: Parameters<typeof patchStage>[2]) =>
      patchStage(pipeline.id, stage.id, patch),
    onSuccess: (r) => { onUpdate(r.funnels); toast(t('pip.stageSaved')) },
    onError: () => toast(t('pip.saveErr')),
  })

  const hideMut = useMutation({
    mutationFn: () => patchStage(pipeline.id, stage.id, { hidden: !stage.hidden }),
    onSuccess: (r) => { onUpdate(r.funnels); onClose() },
    onError: () => toast(t('pip.saveErr')),
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteStage(pipeline.id, stage.id),
    onSuccess: (r) => { onUpdate(r.funnels); onClose(); toast(t('pip.stageDeleted')) },
    onError: () => toast(t('pip.saveErr')),
  })

  const moveMut = useMutation({
    mutationFn: (dir: 'up' | 'down') => {
      const idx = allStages.findIndex((s) => s.id === stage.id)
      if (idx < 0) return Promise.reject()
      const newIdx = dir === 'up' ? idx - 1 : idx + 1
      if (newIdx < 0 || newIdx >= allStages.length) return Promise.reject()
      const ids = allStages.map((s) => s.id)
      ids.splice(idx, 1)
      ids.splice(newIdx, 0, stage.id)
      return reorderStages(pipeline.id, ids)
    },
    onSuccess: (r) => { onUpdate(r.funnels); onClose() },
    onError: () => toast(t('pip.saveErr')),
  })

  const stageIdx = allStages.findIndex((s) => s.id === stage.id)
  const isNative = stage.origin === 'native'
  const busy = patchMut.isPending || hideMut.isPending || deleteMut.isPending || moveMut.isPending

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 96px' }}>
      {/* Back */}
      <button
        onClick={onClose}
        className="row middle"
        style={{ gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, marginBottom: 10, color: 'var(--brand-700)' }}
      >
        <Icon.ArrowRight size={16} sw={2} style={{ transform: 'rotate(180deg)' }} />
        <span className="t-body-s">{t('pip.backToFunnel')}</span>
      </button>

      <div className="row middle" style={{ gap: 8, marginBottom: 16 }}>
        <div className="t-h1">{stage.display_name}</div>
        {isNative && <Phase2Badge />}
      </div>

      {isNative && (
        <div className="card card-16" style={{ marginBottom: 16, background: 'var(--warning-bg)' }}>
          <div className="t-caption" style={{ color: 'var(--warning)' }}>{t('pip.nativeStageNote')}</div>
        </div>
      )}

      {/* Edit fields */}
      <SectionHeader>{t('pip.stageDisplay')}</SectionHeader>
      <div className="card card-16" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <span style={{ font: '600 12px/1.3 var(--font-sans)', color: 'var(--text-2)', marginBottom: 4, display: 'block' }}>
            {t('pip.stageName')}
          </span>
          <input
            style={inputStyle}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('pip.stageNamePh')}
          />
        </div>
        <div>
          <span style={{ font: '600 12px/1.3 var(--font-sans)', color: 'var(--text-2)', marginBottom: 4, display: 'block' }}>
            {t('pip.stageColor')}
          </span>
          <input
            style={inputStyle}
            value={color}
            onChange={(e) => setColor(e.target.value)}
            placeholder="#3b82f6"
          />
        </div>
        <div>
          <span style={{ font: '600 12px/1.3 var(--font-sans)', color: 'var(--text-2)', marginBottom: 4, display: 'block' }}>
            {t('pip.stageSemantics')}
          </span>
          <input
            style={inputStyle}
            value={semantics}
            onChange={(e) => setSemantics(e.target.value)}
            placeholder={t('pip.stageSemanticsPh')}
          />
        </div>
      </div>

      {/* Reorder */}
      <SectionHeader>{t('pip.stageOrder')}</SectionHeader>
      <div className="card card-16" style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <Button
          variant="secondary"
          sm
          disabled={stageIdx <= 0 || busy}
          icon={<Icon.ArrowUp size={15} sw={2} />}
          onClick={() => moveMut.mutate('up')}
        >
          {t('pip.moveUp')}
        </Button>
        <Button
          variant="secondary"
          sm
          disabled={stageIdx >= allStages.length - 1 || busy}
          icon={<Icon.ArrowDown size={15} sw={2} />}
          onClick={() => moveMut.mutate('down')}
        >
          {t('pip.moveDown')}
        </Button>
      </div>

      {/* Danger zone */}
      <SectionHeader>{t('pip.stageActions')}</SectionHeader>
      <div className="card card-16" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button
          variant="secondary"
          sm
          disabled={busy}
          onClick={() => hideMut.mutate()}
        >
          {stage.hidden ? t('pip.unhideStage') : t('pip.hideStage')}
        </Button>
        <Button
          variant="secondary"
          sm
          disabled={busy}
          style={{ color: 'var(--danger)' }}
          onClick={() => {
            if (window.confirm(t('pip.stageDeleteConfirm'))) deleteMut.mutate()
          }}
        >
          {t('pip.deleteStage')}
        </Button>
      </div>

      {/* Sticky save */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, padding: '12px 16px',
        background: 'var(--surface-1)', borderTop: '1px solid var(--border)', maxWidth: 420, margin: '0 auto',
      }}>
        <Button
          full
          disabled={busy || !displayName.trim()}
          onClick={() => patchMut.mutate({ display_name: displayName, color, semantics })}
        >
          {patchMut.isPending ? t('pip.saving') : t('pip.save')}
        </Button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Funnel stage list (second-level view)
// ─────────────────────────────────────────────────────────────────────────────
function FunnelEditor({
  pipeline,
  onClose,
  onUpdate,
}: {
  pipeline: Pipeline
  onClose: () => void
  onUpdate: (funnels: Pipeline[]) => void
}) {
  const toast = useToast()
  const [editingStage, setEditingStage] = useState<PipelineStage | null>(null)
  const [newStageName, setNewStageName] = useState('')
  const [showAddStage, setShowAddStage] = useState(false)

  const addStageMut = useMutation({
    mutationFn: () => createStage(pipeline.id, { name: newStageName.trim() }),
    onSuccess: (r) => {
      onUpdate(r.funnels)
      setNewStageName('')
      setShowAddStage(false)
      toast(t('pip.stageAdded'))
    },
    onError: () => toast(t('pip.saveErr')),
  })

  // Always work from the latest pipeline state (parent keeps funnels fresh)
  const stages = pipeline.stages

  if (editingStage) {
    // Find the fresh version of this stage in the current pipeline
    const freshStage = stages.find((s) => s.id === editingStage.id) ?? editingStage
    return (
      <StageEditor
        pipeline={pipeline}
        stage={freshStage}
        allStages={stages}
        onClose={() => setEditingStage(null)}
        onUpdate={onUpdate}
      />
    )
  }

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 32px' }}>
      {/* Back */}
      <button
        onClick={onClose}
        className="row middle"
        style={{ gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, marginBottom: 10, color: 'var(--brand-700)' }}
      >
        <Icon.ArrowRight size={16} sw={2} style={{ transform: 'rotate(180deg)' }} />
        <span className="t-body-s">{t('pip.backToFunnels')}</span>
      </button>

      <div className="row middle" style={{ gap: 8, marginBottom: 4 }}>
        <div className="t-h1">{pipeline.name}</div>
        {pipeline.origin === 'native' && <Phase2Badge />}
      </div>
      <div className="t-caption dim" style={{ marginBottom: 16 }}>
        {pipeline.origin === 'bitrix' ? t('pip.originBitrix') : t('pip.originNative')}
      </div>

      {/* Stage list */}
      <SectionHeader>{t('pip.stages')}</SectionHeader>
      <div className="card" style={{ padding: 0, marginBottom: 12 }}>
        {stages.length === 0 && (
          <div className="t-caption dim" style={{ padding: 16, textAlign: 'center' }}>{t('pip.stagesEmpty')}</div>
        )}
        {stages.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setEditingStage(s)}
            className="row middle gap-3"
            style={{
              width: '100%', padding: '13px 16px', border: 'none', background: 'transparent',
              cursor: 'pointer', textAlign: 'left',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
            }}
          >
            {s.color && (
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            )}
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div className="row middle" style={{ gap: 4 }}>
                <span className="t-body-s ellipsis">{s.display_name}</span>
                {s.origin === 'native' && <Phase2Badge />}
                {s.hidden && (
                  <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>({t('pip.hidden')})</span>
                )}
              </div>
              {s.semantics && (
                <div className="t-caption dim ellipsis">{s.semantics}</div>
              )}
            </div>
            <Icon.ArrowRight size={18} sw={2} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
          </button>
        ))}
      </div>

      {/* Add stage */}
      {showAddStage ? (
        <div className="card card-16" style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            autoFocus
            style={inputStyle}
            value={newStageName}
            onChange={(e) => setNewStageName(e.target.value)}
            placeholder={t('pip.stageNamePh')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newStageName.trim() && !addStageMut.isPending) addStageMut.mutate()
              if (e.key === 'Escape') { setShowAddStage(false); setNewStageName('') }
            }}
          />
          <div className="row" style={{ gap: 8 }}>
            <Button
              sm
              disabled={!newStageName.trim() || addStageMut.isPending}
              onClick={() => addStageMut.mutate()}
            >
              {addStageMut.isPending ? t('pip.saving') : t('pip.addStage')}
            </Button>
            <Button variant="secondary" sm onClick={() => { setShowAddStage(false); setNewStageName('') }}>
              {t('btn.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          sm
          icon={<Icon.Plus size={15} sw={2} />}
          onClick={() => setShowAddStage(true)}
        >
          {t('pip.addStage')}
        </Button>
      )}

      {/* Custom card sections (director-defined, employee-filled) */}
      <div style={{ marginTop: 20 }}>
        <CardSectionsEditor pipelineId={pipeline.id} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Root funnel list
// ─────────────────────────────────────────────────────────────────────────────
export function PipelinesScreen() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['pipelines'],
    queryFn: fetchPipelines,
  })

  // Local funnels state — seeded from query, updated after each mutation.
  const [funnels, setFunnels] = useState<Pipeline[] | null>(null)
  const activeFunnels = funnels ?? data?.funnels ?? []

  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null)
  const [showAddFunnel, setShowAddFunnel] = useState(false)
  const [newFunnelName, setNewFunnelName] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  function onUpdate(updated: Pipeline[]) {
    setFunnels(updated)
    // Also sync the RQ cache so a re-mount picks up fresh data.
    qc.setQueryData(['pipelines'], { funnels: updated })
  }

  const importMut = useMutation({
    mutationFn: importPipelines,
    onSuccess: (r) => { onUpdate(r.funnels); toast(t('pip.importDone')) },
    onError: () => toast(t('pip.importErr')),
  })

  const createMut = useMutation({
    mutationFn: () => createPipeline(newFunnelName.trim()),
    onSuccess: (r) => {
      onUpdate(r.funnels)
      setNewFunnelName('')
      setShowAddFunnel(false)
      toast(t('pip.funnelCreated'))
    },
    onError: () => toast(t('pip.saveErr')),
  })

  const patchMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof patchPipeline>[1] }) =>
      patchPipeline(id, patch),
    onSuccess: (r) => { onUpdate(r.funnels); setRenamingId(null); toast(t('pip.funnelSaved')) },
    onError: () => toast(t('pip.saveErr')),
  })

  // Seed local state when query resolves for the first time.
  if (!funnels && data) setFunnels(data.funnels)

  // Navigate into an editing pipeline — find it in freshest state
  if (editingPipeline) {
    const freshPipeline = activeFunnels.find((p) => p.id === editingPipeline.id) ?? editingPipeline
    return (
      <FunnelEditor
        pipeline={freshPipeline}
        onClose={() => setEditingPipeline(null)}
        onUpdate={onUpdate}
      />
    )
  }

  if (isLoading) return <ScreenSkeleton />

  if (isError) return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 32px' }}>
      <div className="t-h1" style={{ marginBottom: 16 }}>{t('pip.title')}</div>
      <div className="card card-16">
        <div className="t-body-s" style={{ color: 'var(--danger)', marginBottom: 12 }}>{t('pip.loadErr')}</div>
        <Button variant="secondary" sm icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>
          {t('btn.retry')}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 32px' }}>
      <div className="t-h1" style={{ marginBottom: 4 }}>{t('pip.title')}</div>
      <div className="t-body-s dim" style={{ marginBottom: 16 }}>{t('pip.intro')}</div>

      {/* Import from Bitrix */}
      <div className="card card-16" style={{ marginBottom: 20 }}>
        <div className="t-body-s" style={{ fontWeight: 600, marginBottom: 4 }}>{t('pip.importTitle')}</div>
        <div className="t-caption dim" style={{ marginBottom: 10 }}>{t('pip.importHint')}</div>
        <Button
          variant="secondary"
          sm
          icon={<Icon.Refresh size={15} sw={2} />}
          disabled={importMut.isPending}
          onClick={() => importMut.mutate()}
        >
          {importMut.isPending ? t('pip.importing') : t('pip.importBtn')}
        </Button>
      </div>

      {/* Funnel list */}
      <SectionHeader>{t('pip.funnels')}</SectionHeader>
      {activeFunnels.length === 0 && (
        <div className="t-caption dim" style={{ padding: '8px 0 12px' }}>{t('pip.funnelsEmpty')}</div>
      )}
      {activeFunnels.map((p) => {
        const isRenaming = renamingId === p.id
        return (
          <div key={p.id} className="card" style={{ padding: 0, marginBottom: 8 }}>
            {isRenaming ? (
              <div className="card-16" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  autoFocus
                  style={inputStyle}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && renameValue.trim()) patchMut.mutate({ id: p.id, patch: { name: renameValue.trim() } })
                    if (e.key === 'Escape') setRenamingId(null)
                  }}
                />
                <div className="row" style={{ gap: 8 }}>
                  <Button sm disabled={!renameValue.trim() || patchMut.isPending} onClick={() => patchMut.mutate({ id: p.id, patch: { name: renameValue.trim() } })}>
                    {patchMut.isPending ? t('pip.saving') : t('pip.save')}
                  </Button>
                  <Button variant="secondary" sm onClick={() => setRenamingId(null)}>{t('btn.cancel')}</Button>
                </div>
              </div>
            ) : (
              <>
                {/* Tap entire row → stage editor */}
                <button
                  onClick={() => setEditingPipeline(p)}
                  className="row middle gap-3"
                  style={{ width: '100%', padding: '14px 16px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                >
                  <span style={iconWrap}>
                    <Icon.Layers size={17} sw={2} />
                  </span>
                  <div className="flex-1" style={{ minWidth: 0 }}>
                    <div className="row middle" style={{ gap: 4 }}>
                      <span className="t-body-s ellipsis">{p.name}</span>
                      {p.origin === 'native' && <Phase2Badge />}
                      {p.is_default && (
                        <span style={{ fontSize: 11, color: 'var(--brand-700)', marginLeft: 4, fontWeight: 600 }}>
                          {t('pip.default')}
                        </span>
                      )}
                      {p.is_archived && (
                        <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>
                          {t('pip.archived')}
                        </span>
                      )}
                    </div>
                    <div className="t-caption dim">{p.stages.length} {t('pip.stagesCount')}</div>
                  </div>
                  <Icon.ArrowRight size={18} sw={2} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
                </button>

                {/* Action row below the main tap target */}
                <div className="row" style={{ gap: 6, padding: '0 16px 12px', flexWrap: 'wrap' }}>
                  <button
                    className="chip"
                    onClick={(e) => { e.stopPropagation(); setRenamingId(p.id); setRenameValue(p.name) }}
                    style={{ cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent' }}
                  >
                    {t('pip.rename')}
                  </button>
                  {!p.is_default && (
                    <button
                      className="chip"
                      onClick={(e) => { e.stopPropagation(); patchMut.mutate({ id: p.id, patch: { is_default: true } }) }}
                      style={{ cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent' }}
                    >
                      {t('pip.setDefault')}
                    </button>
                  )}
                  <button
                    className="chip"
                    onClick={(e) => { e.stopPropagation(); patchMut.mutate({ id: p.id, patch: { is_archived: !p.is_archived } }) }}
                    style={{ cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent' }}
                  >
                    {p.is_archived ? t('pip.unarchive') : t('pip.archive')}
                  </button>
                </div>
              </>
            )}
          </div>
        )
      })}

      {/* Add funnel */}
      {showAddFunnel ? (
        <div className="card card-16" style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            autoFocus
            style={inputStyle}
            value={newFunnelName}
            onChange={(e) => setNewFunnelName(e.target.value)}
            placeholder={t('pip.funnelNamePh')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFunnelName.trim() && !createMut.isPending) createMut.mutate()
              if (e.key === 'Escape') { setShowAddFunnel(false); setNewFunnelName('') }
            }}
          />
          <div className="row" style={{ gap: 8 }}>
            <Button sm disabled={!newFunnelName.trim() || createMut.isPending} onClick={() => createMut.mutate()}>
              {createMut.isPending ? t('pip.saving') : t('pip.createFunnel')}
            </Button>
            <Button variant="secondary" sm onClick={() => { setShowAddFunnel(false); setNewFunnelName('') }}>
              {t('btn.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          sm
          icon={<Icon.Plus size={15} sw={2} />}
          style={{ marginTop: 4 }}
          onClick={() => setShowAddFunnel(true)}
        >
          {t('pip.addFunnel')}
        </Button>
      )}
    </div>
  )
}
